import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { markAsRead, deleteNotification } from '../../../services/notificationService';

const NotificationCard = ({ notification, onUpdate }) => {
  const handleMarkAsRead = async () => {
    if (notification?.isRead) return;
    
    const { error } = await markAsRead(notification?.id);
    if (!error) {
      onUpdate();
    }
  };

  const handleDelete = async () => {
    const { error } = await deleteNotification(notification?.id);
    if (!error) {
      onUpdate();
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'ai_match':
        return { name: 'Sparkles', color: 'text-purple-500' };
      case 'deal_progress':
        return { name: 'TrendingUp', color: 'text-blue-500' };
      case 'market_value':
        return { name: 'DollarSign', color: 'text-green-500' };
      case 'scout_response':
        return { name: 'MessageSquare', color: 'text-orange-500' };
      case 'transfer_inquiry':
        return { name: 'Send', color: 'text-cyan-500' };
      case 'profile_view':
        return { name: 'Eye', color: 'text-indigo-500' };
      case 'club_interest':
        return { name: 'Heart', color: 'text-red-500' };
      default:
        return { name: 'Bell', color: 'text-primary' };
    }
  };

  const getNotificationTypeLabel = (type) => {
    switch (type) {
      case 'ai_match':
        return 'AI Match';
      case 'deal_progress':
        return 'Deal Progress';
      case 'market_value':
        return 'Market Value';
      case 'scout_response':
        return 'Scout Response';
      case 'transfer_inquiry':
        return 'Transfer Inquiry';
      case 'profile_view':
        return 'Profile View';
      case 'club_interest':
        return 'Club Interest';
      default:
        return 'Notification';
    }
  };

  const formatTimeAgo = (timestamp) => {
    const now = new Date();
    const notificationTime = new Date(timestamp);
    const diffMs = now - notificationTime;
    const diffSecs = Math.floor(diffMs / 1000);
    const diffMins = Math.floor(diffSecs / 60);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffSecs < 60) return `${diffSecs}s ago`;
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return notificationTime?.toLocaleDateString();
  };

  const iconConfig = getNotificationIcon(notification?.notificationType);

  return (
    <div
      className={`bg-card border rounded-lg p-4 hover:shadow-md transition-all duration-200 ${
        notification?.isRead ? 'border-border' : 'border-primary/30 bg-primary/5'
      }`}
      onClick={handleMarkAsRead}
    >
      <div className="flex items-start gap-4">
        {/* Icon */}
        <div className={`w-10 h-10 rounded-full bg-muted flex items-center justify-center flex-shrink-0 ${iconConfig?.color}`}>
          <Icon name={iconConfig?.name} size={20} />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2 mb-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs font-medium text-muted-foreground">
                {getNotificationTypeLabel(notification?.notificationType)}
              </span>
              {!notification?.isRead && (
                <span className="w-2 h-2 rounded-full bg-primary" />
              )}
            </div>
            <span className="text-xs text-muted-foreground whitespace-nowrap">
              {formatTimeAgo(notification?.createdAt)}
            </span>
          </div>

          <h3 className="text-sm font-semibold text-foreground mb-1">
            {notification?.title}
          </h3>
          <p className="text-sm text-muted-foreground mb-3">
            {notification?.message}
          </p>

          {/* Metadata */}
          {notification?.metadata && Object.keys(notification?.metadata)?.length > 0 && (
            <div className="bg-muted/50 rounded-lg p-3 mb-3">
              <div className="grid grid-cols-2 gap-2 text-xs">
                {notification?.metadata?.playerName && (
                  <div>
                    <span className="text-muted-foreground">Player:</span>
                    <span className="ml-1 font-medium text-foreground">
                      {notification?.metadata?.playerName}
                    </span>
                  </div>
                )}
                {notification?.metadata?.clubName && (
                  <div>
                    <span className="text-muted-foreground">Club:</span>
                    <span className="ml-1 font-medium text-foreground">
                      {notification?.metadata?.clubName}
                    </span>
                  </div>
                )}
                {notification?.metadata?.amount && (
                  <div>
                    <span className="text-muted-foreground">Amount:</span>
                    <span className="ml-1 font-medium text-foreground">
                      {notification?.metadata?.amount}
                    </span>
                  </div>
                )}
                {notification?.metadata?.status && (
                  <div>
                    <span className="text-muted-foreground">Status:</span>
                    <span className="ml-1 font-medium text-foreground capitalize">
                      {notification?.metadata?.status}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center gap-2">
            {!notification?.isRead && (
              <Button
                variant="ghost"
                size="sm"
                onClick={(e) => {
                  e?.stopPropagation();
                  handleMarkAsRead();
                }}
                iconName="Check"
                iconPosition="left"
              >
                Mark as Read
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => {
                e?.stopPropagation();
                handleDelete();
              }}
              iconName="Trash2"
              iconPosition="left"
            >
              Delete
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotificationCard;